import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export const OptionsPageShell = ({ title, description, eyebrow = 'GENI Options', actions, children, hideHeader = false }) => {
  const navigate = useNavigate();
  const { isDemoMode, exitDemoMode } = useOptionsMode();
  const { t } = useOptionsLocale();

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white font-body" data-testid="options-page-shell-root">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-8 px-4 py-8 md:px-8 lg:gap-10 lg:py-10">
        {!hideHeader ? (
          <section
            className="overflow-hidden rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(0,230,118,0.12),_transparent_34%),linear-gradient(135deg,_rgba(255,255,255,0.03),_rgba(255,255,255,0.01))] p-6 sm:p-8"
            data-testid="options-page-shell"
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl space-y-4">
                <Badge
                  className="w-fit rounded-full border border-[#00E676]/20 bg-[#00E676]/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-[#9BFFBE]"
                  data-testid="options-shell-badge"
                >
                  {eyebrow}
                </Badge>
                <div className="space-y-3">
                  <h1 className="text-4xl font-black font-heading tracking-tight sm:text-5xl lg:text-6xl" data-testid="options-shell-title">
                    {title}
                  </h1>
                  <p className="max-w-2xl text-sm text-white/65 sm:text-base" data-testid="options-shell-description">
                    {description}
                  </p>
                </div>
              </div>
              {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
            </div>
          </section>
        ) : null}

        {hideHeader && actions ? (
          <div className="flex w-full flex-wrap justify-end gap-3" data-testid="options-page-shell-inline-actions">
            {actions}
          </div>
        ) : null}

        {children}

        {isDemoMode ? (
          <section className="flex flex-col gap-4 rounded-[28px] border border-[#60A5FA]/18 bg-[linear-gradient(135deg,rgba(15,23,42,0.9),rgba(96,165,250,0.08))] px-6 py-5 lg:flex-row lg:items-center lg:justify-between" data-testid="options-demo-footer">
            <div className="space-y-1.5" data-testid="options-demo-footer-copy">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D6E8FF]" data-testid="options-demo-footer-title">
                {t('shell.demoFooterTitle')}
              </p>
              <p className="text-sm text-white/72" data-testid="options-demo-footer-description">
                {t('shell.demoFooterDescription')}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center" data-testid="options-demo-footer-actions">
              <Button
                onClick={() => {
                  exitDemoMode();
                  navigate('/options/connect');
                }}
                data-testid="options-demo-footer-connect-button"
              >
                {t('shell.demoFooterPrimaryCta')}
              </Button>
              <Button
                variant="ghost"
                className="text-white/65 hover:bg-white/5 hover:text-white"
                onClick={() => {
                  exitDemoMode();
                  navigate('/options');
                }}
                data-testid="options-demo-footer-exit-button"
              >
                {t('shell.demoFooterSecondaryCta')}
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};